<template>
  <div class="page-container">
    <main class="main-content">
      <div class="page-inner-box detail-header">
        <el-breadcrumb separator-class="el-icon-arrow-right">
          <el-breadcrumb-item to="/">首页</el-breadcrumb-item>
          <el-breadcrumb-item>所有课程</el-breadcrumb-item>
          <el-breadcrumb-item>项目详情</el-breadcrumb-item>
        </el-breadcrumb>
      </div>

      <div class="page-inner-box project-content">
        <div class="project-detail">
          <div shadow="never" class="project-card">
            <div class="project-title">{{ project.title }}</div>
            <div v-if="project.coverImage" class="project-cover" :style="{ backgroundImage: `url(${project.coverImage})` }"></div>
            <div v-if="project.introHtml" class="project-intro" v-html="project.introHtml"></div>
            <el-empty v-else-if="!loading" description="暂无项目介绍" />

            <div v-if="units && units.length" class="project-units">
              <div class="units-title">项目目录</div>
              <ul class="units-list">
                <li v-for="u in units" :key="u.id || u.title" class="unit-item">{{ u.title }}</li>
              </ul>
            </div>

            <div class="signup-section" v-if="projectId">
              <el-button
                type="primary"
                size="large"
                class="signup-btn"
                :loading="signupSubmitting"
                :disabled="isProjectRegistered"
                @click="handleSignup"
              >{{ isProjectRegistered ? '您已报名' : '我要报名' }}</el-button>
            </div>
          </div>
        </div>
      </div>
    </main>

    <SignupDialog
      ref="signupDialogRef"
      :visible.sync="signupDialogVisible"
      :submitting="signupSubmitting"
      :project-title="project.title"
      @submit="handleSignupSubmit"
      @cancel="handleSignupCancel"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import SignupDialog from '@/components/SignupDialog.vue'
import { getMyProjects, registerProject, uploadFrontendFile, getPersonalInfo } from '@/api/authApi'
import { getProjectDetail } from '@/api/courseApi'

const defaultProjectCover = require('@/assets/image/study_placeholder.png')

export default {
  name: 'ProjectDetail',
  components: { SignupDialog },
  data() {
    return {
      projectId: null,
      loading: false,
      project: { title: '', introHtml: '', coverImage: '' },
      units: [],
      signupSubmitting: false,
      signupDialogVisible: false,
      myProjectRegisteredMap: {},
      myProjectRegisteredAt: 0,
      myProjectRegisteredLoading: false
    }
  },
  computed: {
    ...mapGetters('auth', ['isLoggedIn', 'userInfo', 'isRealNameVerified']),
    isProjectRegistered() {
      if (!this.projectId) return false
      return Boolean(this.myProjectRegisteredMap && this.myProjectRegisteredMap[String(this.projectId)] === true)
    }
  },
  created() {
    this.initFromRoute()
  },
  mounted() {
    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('auth:login-success', this.handleAuthLoginSuccess)
    }
  },
  beforeDestroy() {
    if (typeof window !== 'undefined' && window.removeEventListener) {
      window.removeEventListener('auth:login-success', this.handleAuthLoginSuccess)
    }
  },
  watch: {
    '$route.params.id': 'initFromRoute',
    '$route': {
      handler(to, from) {
        if (to.name === 'projectDetail' && from.name === 'memberSettings') {
          this.refreshUserInfo()
        }
      }
    },
    isLoggedIn: {
      handler(val) {
        if (val) {
          this.ensureMyProjectRegisteredMap(true)
        }
      },
      immediate: true
    }
  },
  methods: {
    handleAuthLoginSuccess() {
      this.refreshUserInfo()
      this.ensureMyProjectRegisteredMap(true)
    },
    async refreshUserInfo() {
      try {
        const res = await getPersonalInfo()
        console.log('ProjectDetail - getPersonalInfo 返回:', res)
        if (res && res.data) {
          console.log('ProjectDetail - isRealNameVerified:', res.data.isRealNameVerified)
          this.$store.dispatch('auth/updateUserInfo', res.data)
        }
      } catch (e) {
        void e
      }
    },
    initFromRoute() {
      const id = this.$route.params.id
      this.projectId = id
      if (this.isLoggedIn) {
        this.refreshUserInfo()
      }
      this.fetchProject()
    },
    sanitizeUrl(val) {
      const s = String(val || '').trim()
      if (!s) return ''
      return s.replace(/\\`/g, '').replace(/`/g, '').trim()
    },
    sanitizeRichText(html) {
      const raw = String(html || '')
      if (!raw.trim()) return ''
      const cleaned = raw.replace(/\\`/g, '').replace(/`/g, '')
      try {
        const parser = new DOMParser()
        const doc = parser.parseFromString(cleaned, 'text/html')
        doc.querySelectorAll('script').forEach(node => node.remove())
        doc.querySelectorAll('*').forEach(el => {
          Array.from(el.attributes || []).forEach(attr => {
            const name = String(attr.name || '').toLowerCase()
            if (name.startsWith('on')) el.removeAttribute(attr.name)
          })
          if (el.hasAttribute('href')) {
            const href = this.sanitizeUrl(el.getAttribute('href'))
            if (/^javascript:/i.test(href)) el.removeAttribute('href')
            else el.setAttribute('href', href)
          }
          if (el.hasAttribute('src')) {
            const src = this.sanitizeUrl(el.getAttribute('src'))
            if (/^javascript:/i.test(src)) el.removeAttribute('src')
            else el.setAttribute('src', src)
          }
        })
        return doc.body.innerHTML || ''
      } catch (e) {
        return cleaned
      }
    },
    async fetchProject() {
      if (!this.projectId || this.loading) return
      this.loading = true
      try {
        const res = await getProjectDetail(this.projectId)
        const data = res && res.data
        
        if (data) {
          this.project = {
            title: String(data.projectName || data.title || ''),
            introHtml: this.sanitizeRichText(data.description || data.introHtml || ''),
            coverImage: this.sanitizeUrl(data.coverImage) || defaultProjectCover
          }
          this.units = Array.isArray(data.courseIds) ? data.courseIds.map((id, i) => ({ id, title: `课程${i + 1}` })) : []
          
          // Update registration status from API response
          if (data.isRegistered !== undefined) {
            this.myProjectRegisteredMap[String(this.projectId)] = data.isRegistered
          }
          
          // Update real-name verification status
          if (data.isRealNameVerified !== undefined && this.isLoggedIn) {
            this.$store.dispatch('auth/updateUserInfo', { isRealNameVerified: data.isRealNameVerified })
          }
        } else {
          this.project = { title: '', introHtml: '', coverImage: defaultProjectCover }
          this.units = []
        }
      } catch (err) {
        console.error('获取项目详情失败:', err)
        this.project = { title: '', introHtml: '', coverImage: defaultProjectCover }
        this.units = []
      } finally {
        this.loading = false
      }
    },
    extractListFromApiData(data) {
      if (Array.isArray(data)) return data
      if (data && Array.isArray(data.list)) return data.list
      if (data && Array.isArray(data.records)) return data.records
      if (data && Array.isArray(data.items)) return data.items
      return []
    },
    normalizeIsRegistered(val) {
      if (val === true || val === 1 || val === '1' || val === 'true') return true
      if (val === false || val === 0 || val === '0' || val === 'false') return false
      return !!val
    },
    async ensureMyProjectRegisteredMap(force = false) {
      if (!this.isLoggedIn) return
      if (this.myProjectRegisteredLoading) return
      const now = Date.now()
      const ttlMs = 30 * 1000
      if (!force && this.myProjectRegisteredAt && now - this.myProjectRegisteredAt < ttlMs) return
      this.myProjectRegisteredLoading = true
      try {
        const res = await getMyProjects({ pageNum: 1, pageSize: 200 })
        const list = this.extractListFromApiData(res && res.data)
        const map = {}
        list.forEach((raw) => {
          const projectId = raw && (raw.projectId ?? raw.id)
          if (projectId == null || projectId === '') return
          const isRegistered =
            this.normalizeIsRegistered(raw && raw.isRegistered) ||
            this.normalizeIsRegistered(raw && raw.is_registered) ||
            this.normalizeIsRegistered(raw && raw.registered) ||
            Boolean(raw && (raw.registrationTime || raw.registerTime || raw.register_time))
          map[String(projectId)] = Boolean(isRegistered)
        })
        this.myProjectRegisteredMap = map
        this.myProjectRegisteredAt = now
      } catch (e) {
        this.myProjectRegisteredMap = {}
        this.myProjectRegisteredAt = now
      } finally {
        this.myProjectRegisteredLoading = false
      }
    },
    async handleSignup() {
      console.log('ProjectDetail - 实名认证状态:', this.isRealNameVerified, 'userInfo:', this.userInfo)
      if (!this.projectId) {
        this.$message.error('缺少项目ID，无法报名')
        return
      }
      if (!this.isLoggedIn) {
        this.$message.error('请先登录')
        return
      }
      if (this.isProjectRegistered) {
        this.$message.success('您已报名该项目')
        return
      }
      if (!this.isRealNameVerified) {
        this.forceRealNameVerification()
        return
      }
      this.signupDialogVisible = true
    },
    async forceRealNameVerification() {
      try {
        await this.$confirm('报名前需要完成实名认证，是否现在去实名认证？', '实名认证提醒', {
          confirmButtonText: '去认证',
          cancelButtonText: '取消',
          type: 'warning',
          showClose: false,
          closeOnClickModal: false,
          closeOnPressEscape: false,
          distinguishCancelAndClose: true
        })
        this.$router.push('/member-center/settings').catch(() => {})
      } catch (e) {
        void e
      }
    },
    handleSignupCancel() {
      this.signupDialogVisible = false
    },
    normalizeUploadedUrl(res) {
      const data = res && res.data
      if (typeof data === 'string') return data
      if (!data || typeof data !== 'object') return ''
      return (
        data.url ||
        data.fileUrl ||
        data.fileURL ||
        data.path ||
        data.filePath ||
        data.fullPath ||
        data.downloadUrl ||
        ''
      )
    },
    async handleSignupSubmit(form) {
      if (this.signupSubmitting) return
      if (!this.projectId) {
        this.$message.error('缺少项目ID，无法报名')
        return
      }
      if (!this.isLoggedIn) {
        this.$message.error('请先登录')
        return
      }
      if (this.isProjectRegistered) {
        this.signupDialogVisible = false
        this.$message.success('您已报名该项目')
        return
      }
      if (!this.isRealNameVerified) {
        this.forceRealNameVerification()
        return
      }
      this.signupSubmitting = true
      try {
        const files = form && Array.isArray(form.uploadFiles) ? form.uploadFiles : []
        const uploadUrls = []
        for (const file of files) {
          const res = await uploadFrontendFile(file, `project-registration/${this.projectId}`)
          const url = this.normalizeUploadedUrl(res)
          if (url) uploadUrls.push(url)
        }

        const payload = {
          ...(form || {}),
          uploadFiles: uploadUrls,
          courseId: this.projectId
        }
        const res = await registerProject(this.projectId, payload)
        if (res && Number(res.code) === 200) {
          this.$message.success(res.message || '报名成功')
          await this.ensureMyProjectRegisteredMap(true)
          this.signupDialogVisible = false
          if (this.$refs.signupDialogRef && this.$refs.signupDialogRef.reset) {
            this.$refs.signupDialogRef.reset()
          }
          return
        }
        this.$message.error((res && (res.message || res.msg)) || '报名失败')
      } catch (err) {
        this.$message.error((err && err.message) || '报名失败')
      } finally {
        this.signupSubmitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f5f7fa;
}

.main-content {
  flex: 1;
  padding-top: 20px;
}

.page-inner-box {
  max-width: 1200px;
  margin: 0 auto;
  background-color: white;
  border-radius: 4px;
  padding: 20px;
  margin-bottom: 20px;
}

.detail-header {
  margin-bottom: 20px;
  background-color: #f5f7fa;
}

.project-content {
  .project-detail {
    max-width: 800px;
    margin: 0 auto;
  }
}

.project-card {
  border-radius: 4px;
  padding: 24px;
  margin-top: 15px;
  box-shadow: none;
}

.project-title {
  font-size: 28px;
  font-weight: bold;
  color: #333;
  text-align: center;
  margin-bottom: 25px;
}

.project-intro {
  font-size: 14px;
  line-height: 1.8;
  color: #666;
  margin-bottom: 20px;
  text-align: justify;
}

.project-cover {
  width: 100%;
  height: 320px;
  border-radius: 10px;
  background: #f5f7fa;
  background-size: cover;
  background-position: center;
  margin-bottom: 20px;
}

.project-units {
  margin-top: 18px;
}

.units-title {
  font-weight: 600;
  color: #303133;
  margin-bottom: 10px;
}

.units-list {
  padding-left: 18px;
  color: #606266;
  line-height: 1.8;
}

.unit-item {
  margin-bottom: 4px;
}

.signup-section {
  margin-top: 30px;
  text-align: center;
  padding: 20px 0;
  border-top: 1px dashed #eee;
}

.signup-btn {
  min-width: 200px;
  height: 48px;
  font-size: 16px;
  font-weight: 600;
  background-color: #3E2CCD !important;
  border-color: #3E2CCD !important;
  border-radius: 8px;
  color: #fff !important;
}

.signup-btn:hover {
  background-color: #3329c7 !important;
  border-color: #3329c7 !important;
}

.signup-btn:disabled {
  background-color: #c0c4cc !important;
  border-color: #c0c4cc !important;
}
</style>